import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded';
import { Avatar, Box, Card, CardActions, CardContent, CardHeader, Menu, MenuItem, Stack, useTheme } from '@mui/material';
import { SyntheticEvent, useEffect, useMemo, useState } from 'react';

import ViewNumberFormat from '@/views/elements/Inputs/ViewNumberFormat';
import ButtonElement from '@/views/elements/button/ButtonElement';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { getInvestPerformancePrivate, getInvestPerformancePublic } from '@/controllers/gridManager/followerTransManager/followerTransManager';
import clsx from 'clsx';
import './CardInfo.scss';

import { getFirstCharacterName } from '@/utilities';
import LineChartElement from '@/views/elements/chart/LineChartElement';
import IconButtonElement from '@/views/elements/iconButton/IconButtonElement';
import TypographyElement from '@/views/elements/typography/TypographyElement';
import RenderProfitLoss from './RenderProfitLoss';
import RenderRank from './RenderRank';
import { useCosInfo } from '@/controllers/gridManager/accountsManager/accountManager';
import { useTranslation } from 'react-i18next';
import { COS_ROLE, STATUS_ACCOUNT } from '@/stores/dataStore/memoryStore/const/constIndex';

type Props = {
    info: any,
    onCopy?: (data: any) => void,
    className: string,
    onSelected?: (data: boolean) => void
    setDataSelected?: (data: any) => void
    status: boolean

    isPrivate: boolean // màn quản lý
    onExtend?: (data: any) => void
    onStop?: (data: any) => void
    onFollow?: (data: any) => void
    onWithdraw?: (data?: any) => void
    onConfig?: (data?: any) => void
    onRegister?: () => void
    onSellStuck?: () => void
    onSellOddLot?: () => void
}

export default function CardInfo({ info, onCopy, className, onSelected,
    setDataSelected, status, isPrivate, onExtend, onStop, onFollow,
    onWithdraw, onConfig, onRegister, onSellStuck, onSellOddLot,
}: Props) {
    const theme = useTheme()
    const { t } = useTranslation()

    const { cosInfo } = useCosInfo()

    const [dataSeries, setDataSeries] = useState<any>([])

    const [openPopper, setOpenPopper] = useState<boolean>(false)
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const { refaccount, cosrole, status: accountStatus } = cosInfo

    const isMasterWaitStopCopy = info?.flstatus === STATUS_ACCOUNT.WC

    useEffect(() => {
        // getInvestPerformanceBy
        async function callApiDataChart() {
            // const isPrivate
            const func = isPrivate === true ? getInvestPerformancePrivate : getInvestPerformancePublic
            const accountId = isPrivate === true ? refaccount : info?.afacctno
            const masterAccount = isPrivate === true ? info?.afacctno : ''
            const role = isPrivate === true ? cosrole : COS_ROLE.M
            const { isSuccess, data } = await func(accountId, masterAccount, role) //info?.afacctno
            const rs = data
            if (isSuccess) {
                setDataSeries(rs)
            }
        }

        callApiDataChart()
    }, [isPrivate, cosrole, refaccount, info])

    const handleOnCopy = ((e: any, info: any) => {
        e.stopPropagation();
        if (isNotRegisteredMF) {
            onRegister && onRegister()
        } else {
            if (disabledByStatusFollower === true) return;
            onCopy && onCopy(info)
        }
    })

    const handleOnFollow = ((e: any, info: any) => {
        e.stopPropagation();
        if (disabledByStatusFollower === true) return;
        onFollow && onFollow(info)
        // onCopy(info)
    })

    const handleOnExtend = ((e: any, info: any) => {
        e.stopPropagation();
        if (disabledByStatusFollower === true) return;
        if (isMasterWaitStopCopy === true) return;
        onExtend && onExtend(info)
    })

    const handleOnStop = ((e: any, info: any) => {
        e.stopPropagation();
        if (disabledByStatusFollower === true) return;
        if (isMasterWaitStopCopy === true) return;
        onStop && onStop(info)
    })

    const handleMenuAction = (e: SyntheticEvent, info: any, option: 'VIEW' | 'CONFIG' | 'WITHDRAW' | 'SELL_STUCK' | 'SELL_ODD_LOT') => {
        e.stopPropagation();
        const disabledMasterStatusWaitStop = ['CONFIG', 'WITHDRAW']
        if (disabledMasterStatusWaitStop.includes(option) && (isMasterWaitStopCopy === true)) {
            return;
        }

        setOpenPopper(false)
        setAnchorEl(null)
        onSelected && onSelected(true)
        setDataSelected && setDataSelected(info)

        if (option === 'VIEW') {
        } else if (option === 'CONFIG') {
            if (disabledByStatusFollower === true) return;
            onConfig && onConfig(info)
        } else if (option === 'WITHDRAW') {
            if (disabledByStatusFollower === true) return;
            onWithdraw && onWithdraw(info)
        } else if (option === 'SELL_STUCK') {
            if (disabledByStatusFollower === true) return;
            onSellStuck && onSellStuck()
        } else if (option === 'SELL_ODD_LOT') {
            if (disabledByStatusFollower === true) return;
            onSellOddLot && onSellOddLot()
        }
    }

    const renderNameAndRank = (name: string, rank: string) => {
        return (
            <Stack direction={'row'} spacing={2}>
                <Box>
                    <TypographyElement isEllipsis={true} sx={{ maxWidth: 200 }} ColorElement="primary" FontSizeElement='14px' FontWeightElement={500}>
                        {name}
                    </TypographyElement>
                </Box>

                {isPrivate !== true && <RenderRank rank={rank} />}
            </Stack>
        )
    }

    const renderPNLAffiliate = (tp: number, sl: number, bonus_fee: number) => {
        return (
            <Stack
                direction={'row'}
                alignItems={"center"}
                spacing={0.5}
                sx={{ marginTop: 0.25 }}
            >
                <Box sx={{ bgcolor: theme.palette.background.badge, width: "auto", px: 1, py: "2px", borderRadius: 1 }}
                    component="span">
                    <TypographyElement FontSizeElement='12px' FontWeightElement={400} ColorElement="secondary" component={'span'}>{t("copytrade.cardInfo.tpsl")}&nbsp;
                        <TypographyElement FontSizeElement='12px' FontWeightElement={400} ColorElement="up" component={'span'}>{tp}%</TypographyElement>/
                        <TypographyElement FontSizeElement='12px' FontWeightElement={400} ColorElement="down" component={'span'}>{sl}%</TypographyElement>
                    </TypographyElement>
                </Box>

                <Box sx={{ bgcolor: theme.palette.background.badge, width: "auto", px: 1, py: "2px", borderRadius: 1 }}
                    component="span">
                    <TypographyElement FontSizeElement='12px' FontWeightElement={400} ColorElement="secondary" component={'span'}>{t("copytrade.cardInfo.bonusFee")} {bonus_fee ?? 0}%</TypographyElement>
                </Box>
            </Stack>
        )
    }

    const renderSubheader = (copier_number: number, follower_number: number) => {
        return (
            <Stack
                direction={'row'}
                alignItems={"center"}
                sx={{ marginTop: 0.25 }}
            >
                <Box sx={{ bgcolor: theme.palette.background.badge, width: "auto", px: 1, py: "2px", borderRadius: 1, fontSize: 12, display: 'flex', gap: '4px' }}
                    component="span">
                    <PeopleOutlineIcon sx={{ fontSize: '18px', paddingBottom: '1px' }} /> <TypographyElement FontSizeElement='12px' FontWeightElement={400} ColorElement="secondary" component={'span'}>{follower_number}</TypographyElement>
                </Box>
            </Stack>
        )
    }

    const renderInfoLastMonth = (r_1_mon: number) => {
        const value = Number.parseFloat(r_1_mon?.toString())
        return (
            <Stack
                direction={'column'}
                spacing={1}
                sx={{ width: '100%' }}
            >
                <TypographyElement ColorElement='secondary' FontSizeElement='12px' FontWeightElement={400}>
                    {t("copytrade.cardInfo.effective1m")}
                </TypographyElement>

                <RenderProfitLoss data={value} />
            </Stack>
        )
    }

    const renderInfoScore = () => {
        return (
            <Stack direction={'row'} spacing={1}>

                <Stack width={"100%"}>
                    <TypographyElement ColorElement='secondary' FontSizeElement='12px' FontWeightElement={400}>
                        {t("copytrade.cardInfo.copyCapital")}
                    </TypographyElement>

                    <ViewNumberFormat value={info?.aum ?? 0} />
                </Stack>

                <Stack width={"100%"}>
                    <TypographyElement ColorElement='secondary' FontSizeElement='12px' FontWeightElement={400}>
                        {t("copytrade.cardInfo.risk")}
                    </TypographyElement>

                    <ViewNumberFormat value={info?.risk_point ?? 0} />
                </Stack>

                <Stack width={"100%"}>
                    <TypographyElement ColorElement='secondary' FontSizeElement='12px' FontWeightElement={400}>
                        {t("copytrade.cardInfo.masterScore")}
                    </TypographyElement>

                    <ViewNumberFormat value={info?.score ?? 0} />
                </Stack>

            </Stack>
        )
    }

    const renderInfoScoreFollowing = () => {
        const valuePnL = Number.parseFloat((info?.invest_balance === 0 ? 0 : (((info?.pnl ?? 0) / (info?.invest_balance ?? 1)) - 1) * 100).toFixed(2))
        const isNegative = valuePnL <= 0
        return (
            <Stack direction={'row'} spacing={1}>

                <Stack width={"100%"}>
                    <TypographyElement ColorElement='secondary' FontSizeElement='12px' FontWeightElement={400}>
                        {t("copytrade.cardInfo.capital")}
                    </TypographyElement>

                    <ViewNumberFormat value={info?.invest_balance ?? 0} />
                </Stack>

                <Stack width={"100%"}>
                    <TypographyElement ColorElement='secondary' FontSizeElement='12px' FontWeightElement={400}>
                        {t("copytrade.cardInfo.ppnl")}
                    </TypographyElement>
                    <TypographyElement ColorElement={!isNegative ? "up" : "down"}>
                        {!isNegative ? '+' : info?.invest_balance === 0 ? '' : ''} <ViewNumberFormat displayType='text' value={valuePnL} isPercentage={true} isCheckPNL={true} />
                    </TypographyElement>
                </Stack>

                <Stack width={"100%"}>
                    <TypographyElement ColorElement='secondary' FontSizeElement='12px' FontWeightElement={400}>
                        {t("copytrade.cardInfo.navCopy")}
                    </TypographyElement>
                    <ViewNumberFormat value={info?.nav_realtime ?? 0} />
                </Stack>

            </Stack>
        )
    }

    const handleSelectedMaster = (info: any) => {
        if (isPrivate === true) {
            onSelected && onSelected(true)
            setDataSelected && setDataSelected(info)
            return;
        }
        onSelected && onSelected(true)
        setDataSelected && setDataSelected(info)
    }

    const handleClickSetting = (event: SyntheticEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setOpenPopper(true);
    };

    const handleCloseSetting = (event: SyntheticEvent) => {
        event.stopPropagation();
        setAnchorEl(null);
        setOpenPopper(false);
    }

    const isNotRegisteredMF = !status

    const disabledByStatusFollower = useMemo(() => {
        return (cosrole === COS_ROLE.F) && (accountStatus === STATUS_ACCOUNT.WC)
    }, [cosrole, accountStatus])

    const disabledByStatusMaster = useMemo(() => {
        return (cosrole === COS_ROLE.M) && (accountStatus === STATUS_ACCOUNT.WC)
    }, [cosrole, accountStatus])

    return (
        <Card onClick={() => handleSelectedMaster(info)}
            className={clsx('card-info-user', className && className)}
            sx={{
                minWidth: 368, maxWidth: 410,
                bgcolor: theme.palette.background.default,
                position: 'relative',
                borderColor: theme.palette.border
            }}
            variant="outlined">

            <Menu
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            backgroundColor: theme.palette.background.menu
                        }
                    }
                }}
                elevation={0}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                id="basic-menu"
                anchorEl={anchorEl}
                open={openPopper}
                onClose={handleCloseSetting}
            >
                <MenuItem onClick={(e: SyntheticEvent) => handleMenuAction(e, info, 'VIEW')}>{t("copytrade.cardInfo.viewMaster")}</MenuItem>
                <MenuItem disabled={isMasterWaitStopCopy} onClick={(e: SyntheticEvent) => handleMenuAction(e, info, 'WITHDRAW')}>{t("copytrade.common.withDraw")}</MenuItem>
                <MenuItem disabled={isMasterWaitStopCopy} onClick={(e: SyntheticEvent) => handleMenuAction(e, info, 'CONFIG')} >{t("copytrade.common.configCopy")}</MenuItem>
                <MenuItem onClick={(e: SyntheticEvent) => handleMenuAction(e, info, 'SELL_STUCK')}>{t("copytrade.common.sellStuck")}</MenuItem>
                <MenuItem onClick={(e: SyntheticEvent) => handleMenuAction(e, info, 'SELL_ODD_LOT')}>{t("copytrade.common.sellOddLot")}</MenuItem>
            </Menu>

            <CardHeader
                avatar={
                    <Avatar sx={{}} aria-label="recipe" variant="rounded">
                        {getFirstCharacterName(info?.mt_fullname)}
                    </Avatar>
                }
                sx={{ padding: 0 }}
                action={isPrivate === true ? (
                    <IconButtonElement disabled={disabledByStatusFollower} customType='Transparent' className={openPopper ? "actived" : ""} onClick={(e: SyntheticEvent<HTMLButtonElement>) => handleClickSetting(e)} aria-label="settings">
                        <MoreVertIcon />
                    </IconButtonElement>
                ) : null
                }
                title={renderNameAndRank(info?.mt_fullname ?? '', info?.mt_rank ?? '')}
                subheader={isPrivate === true ? renderPNLAffiliate(info?.take_profit_rate ?? 0, info?.cut_loss_rate ?? 0, info?.bonus_fee ?? 0) : renderSubheader(info?.copier_number ?? 0, info?.follower_number ?? 0)}
            />
            <CardContent sx={{ px: 0, py: "10px" }}>
                <Stack direction={'row'} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                    {renderInfoLastMonth(info?.r_1_mon ?? info?.twr_1_mon ?? 0)}

                    <Box className="chart" sx={{ height: "80px", width: "100%" }}>
                        <LineChartElement
                            series={[
                                {
                                    id: 'ar',
                                    marker: {
                                        enabled: false
                                    },
                                    name: t("copytrade.cardInfo.chartEffect"),
                                    type: "areaspline",
                                    lineWidth: 1.3
                                },
                                {
                                    id: 'ar_index',
                                    marker: {
                                        enabled: false
                                    },
                                    name: "Index",
                                    isSupportDashStyle: true,
                                    dashStyle: 'Dash',
                                    lineWidth: 1.3
                                }
                            ]}
                            idCompare='ar'
                            dataSeries={dataSeries}
                            isShowLegend={false}
                            isShowXAxis={false}
                            isShowYAxis={false}
                            numberDecimal={2}
                        />
                    </Box>
                </Stack>

                {isPrivate === true ? renderInfoScoreFollowing() : renderInfoScore()}

            </CardContent>

            <CardActions sx={{ px: 0, paddingBottom: 0 }}>
                {isPrivate === true ? (
                    <Stack spacing={2} sx={{ width: "100%" }} direction={"row"} justifyContent={"center"}>
                        <ButtonElement
                            disabled={isNotRegisteredMF || disabledByStatusFollower || isMasterWaitStopCopy}
                            onClick={(e: SyntheticEvent) => handleOnStop(e, info)}
                            customType='Secondary'
                            fullWidth={true}
                            aria-label="add to favorites">
                            <TypographyElement FontSizeElement='14px'>
                                {t('copytrade.common.stopCopy')}
                            </TypographyElement>

                        </ButtonElement>

                        <ButtonElement
                            disabled={isNotRegisteredMF || disabledByStatusFollower || isMasterWaitStopCopy}
                            fullWidth={true}
                            onClick={(e: SyntheticEvent) => handleOnExtend(e, info)}
                            aria-label="share">
                            <TypographyElement FontSizeElement='14px'>
                                {t("copytrade.common.extendInvest")}
                            </TypographyElement>
                        </ButtonElement>
                    </Stack>
                ) : (
                    <Stack spacing={2} sx={{ width: "100%" }} direction={"row"} justifyContent={"center"}>
                        {info?.is_following === 1 ? (
                            <ButtonElement
                                disabled={isNotRegisteredMF || info?.afacctno === refaccount || disabledByStatusFollower || disabledByStatusMaster}
                                onClick={(e: SyntheticEvent) => handleOnFollow(e, info)}
                                sx={{ visibility: info?.afacctno === refaccount ? 'hidden' : 'visible' }}
                                customType='Secondary'
                                fullWidth={true}
                                aria-label="add to favorites">
                                <StarOutlineRoundedIcon sx={{ fontSize: "20px", paddingBottom: '1px' }} /> <TypographyElement FontSizeElement='14px' FontWeightElement={500}>
                                    {t("copytrade.common.unFollow")}
                                </TypographyElement>
                            </ButtonElement>
                        ) : (
                            <ButtonElement
                                disabled={isNotRegisteredMF || info?.afacctno === refaccount || disabledByStatusFollower || disabledByStatusMaster}
                                onClick={(e: SyntheticEvent) => handleOnFollow(e, info)}
                                customType='Secondary'
                                fullWidth={true}
                                aria-label="add to favorites">
                                <StarOutlineRoundedIcon sx={{ fontSize: "20px", paddingBottom: '1px' }} /> <TypographyElement component={'div'} FontSizeElement='14px' FontWeightElement={500}>
                                    {t("copytrade.common.follow")}
                                </TypographyElement>
                            </ButtonElement>
                        )}

                        {cosInfo.cosrole !== COS_ROLE.M &&
                            (info?.is_copying === 1 ? (
                                <ButtonElement
                                    disabled={isNotRegisteredMF || disabledByStatusFollower || isMasterWaitStopCopy}
                                    onClick={(e: SyntheticEvent) => handleOnStop(e, info)}
                                    fullWidth={true}
                                    aria-label="share">
                                    <TypographyElement FontSizeElement='14px' FontWeightElement={500}>
                                        {t('copytrade.common.stopCopy')}
                                    </TypographyElement>

                                </ButtonElement>
                            ) : (
                                <ButtonElement
                                    disabled={disabledByStatusFollower}
                                    className={isNotRegisteredMF ? 'fake-disabled' : ''}
                                    fullWidth={true}
                                    onClick={(e: SyntheticEvent) => handleOnCopy(e, info)}
                                    aria-label="share">
                                    <TypographyElement component={'div'} FontSizeElement='14px' FontWeightElement={500}>
                                        {t("copytrade.common.copy")}
                                    </TypographyElement>
                                </ButtonElement>
                            ))
                        }



                    </Stack>
                )}


            </CardActions>

        </Card>
    )
}